import { PartnerOrganization } from 'khshared/API';
import isDirectToConsumer from 'khshared/isDirectToConsumer';
import getPartnerConfig from 'khshared/metadata/getPartnerConfig';
import React, { useRef } from 'react';
import { ViewStyle } from 'react-native';
import { Redirect } from 'react-router';

import icon from '../../assets/sh-icon.png';
import AppBarLogo from '../../components/AppBarLogo';
import AppointmentOptionItem from '../../components/AppointmentOptionItem';
import BookingProgressIndicator from '../../components/BookingProgressIndicator';
import WizardLayout from '../../components/WizardLayout';
import { KHAppBar, KHColors, KHMessageView } from '../../khui';
import KHAuth from '../../utils/KHAuth';
import useLogger from '../../utils/useLogger';
import { useWizardState } from '../../utils/useWizardState';
import generateURI from '../../xapis/generateURIXAPI.native';
import { BOOKING_WIZARD } from './index';

const iconURI = generateURI(icon);

const styles = {
  wizardLayout: {
    flex: 1,
  } as ViewStyle,
  options: {
    marginTop: 24,
  } as ViewStyle,
  content: {
    flex: 1,
  } as ViewStyle,
  messageView: {
    flex: 1,
  } as ViewStyle,
};

export default function CoordinatorRoleScreen(): JSX.Element {
  const wizardState = useWizardState(BOOKING_WIZARD, 'coordinator-role-state');
  const { partnerOrganization } = wizardState.context.appointment;
  const logger = useLogger();
  const viewerPartnerOrganizations = useRef<PartnerOrganization[]>(
    KHAuth.getPartnerOrganizations(),
  );
  const isD2C = isDirectToConsumer(wizardState.context.appointment);

  function chooseRoleAndContinue(type: 'me' | 'meAndOthers' | 'others' | 'someoneElse') {
    switch (type) {
      case 'me':
        wizardState.context.coordinatorIsPatient = true;
        wizardState.context.patientsAmount = 'single';
        break;
      case 'meAndOthers':
        wizardState.context.coordinatorIsPatient = true;
        wizardState.context.patientsAmount = 'multiple';
        break;
      case 'others':
        wizardState.context.coordinatorIsPatient = false;
        wizardState.context.patientsAmount = 'multiple';
        break;
      case 'someoneElse':
        wizardState.context.coordinatorIsPatient = false;
        wizardState.context.patientsAmount = 'single';
        break;
      default:
        break;
    }
    wizardState.next({ wizardState, name: 'coordinator-role-continue-action' });
  }

  // If the viewer is a partner CSR, they cannot continue in the D2C booking flow
  if (viewerPartnerOrganizations.current.length > 0) return <Redirect to="/partner/appointments" />;
  // If the user is not signed in and not coming from a partner redirect, redirect to sign in
  if (isD2C && !KHAuth.isSignedIn()) return <Redirect to="/signin" />;
  return (
    <WizardLayout
      appBarLeft={
        <KHAppBar.TextAction
          onPress={async () => {
            await KHAuth.genSignOut(logger);
          }}
          logger={wizardState.logger}
          loggerEventName="signOutButtonClick"
        >
          Sign out
        </KHAppBar.TextAction>
      }
      backDisabled
      style={styles.wizardLayout}
      title={<AppBarLogo partnerOrganization={partnerOrganization} />}
      titleStyle={AppBarLogo.titleStyle}
      subheader={
        !isDirectToConsumer(wizardState.context.appointment) && (
          <BookingProgressIndicator currentStepNumber={1} />
        )
      }
      logger={wizardState.logger}
    >
      {!isD2C ? (
        <WizardLayout logger={wizardState.logger}>
          {' '}
          <WizardLayout.Title>Who are you booking for?</WizardLayout.Title>
          <WizardLayout.Content style={styles.options}>
            <AppointmentOptionItem
              buttonLabel="Me"
              onPress={() => chooseRoleAndContinue('me')}
              backgroundColor={KHColors.backgroundPrimary}
              imageSource="../images/booking/img-solo-sm.webp"
            />
            {getPartnerConfig(partnerOrganization).isMultiPatientBookingAllowed ? (
              <>
                <AppointmentOptionItem
                  buttonLabel="Me &amp; others"
                  onPress={() => chooseRoleAndContinue('meAndOthers')}
                  backgroundColor={KHColors.backgroundPrimaryDark}
                  imageSource="../images/booking/img-family-sm.webp"
                />
                <AppointmentOptionItem
                  buttonLabel="Others"
                  onPress={() => chooseRoleAndContinue('others')}
                  backgroundColor={KHColors.backgroundSecondary}
                  imageSource="../images/booking/img-for-others-sm.webp"
                />
              </>
            ) : (
              <AppointmentOptionItem
                buttonLabel="Someone else"
                onPress={() => chooseRoleAndContinue('someoneElse')}
                backgroundColor={KHColors.backgroundSecondary}
                imageSource="../images/booking/img-for-others-sm.webp"
              />
            )}
          </WizardLayout.Content>
        </WizardLayout>
      ) : (
        <WizardLayout.Content>
          <KHMessageView
            style={styles.messageView}
            icon={{ uri: iconURI }}
            title="Self-booking is no longer available"
            message="For any questions please contact support@sprinterhealth.com"
          />
        </WizardLayout.Content>
      )}
    </WizardLayout>
  );
}
